/**
 * Bulk actions on the domain list (owner 2026-09-29: "1 lần chạm là thao tác được nhiều tính
 * năng"): select domains, pick Gia hạn / Đổi máy chủ tên / Khoá / Mở khoá chuyển / Cập nhật liên
 * hệ theo chủ thể, give one reason and confirm once. Each domain is then sent on its own through
 * the same calls as the domain page (no bulk endpoint), one after another, and the sheet shows
 * each result; failed ones can be retried with the same idempotency keys.
 */

import { CalendarPlus, CheckCircle2, CircleDashed, Loader2, Lock, LockOpen, RefreshCw, Server, X, XCircle } from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { api, type DomainSummary } from '../../api';
import { Callout, Field } from '../../components/blocks';
import { newIdempotencyKey } from '../../lib/ids';
import { errorText as readableError } from '../../lib/errorText';

export type BulkAction = 'renew' | 'nameservers' | 'lock' | 'unlock' | 'syncContacts';

export const BULK_ACTIONS: readonly BulkAction[] = ['renew', 'nameservers', 'lock', 'unlock', 'syncContacts'];

const ICONS: Record<BulkAction, typeof Lock> = { renew: CalendarPlus, nameservers: Server, lock: Lock, unlock: LockOpen, syncContacts: RefreshCw };

type RowState = { phase: 'waiting' } | { phase: 'running' } | { phase: 'ok'; note: string } | { phase: 'failed'; note: string } | { phase: 'skipped'; note: string };

const selectClass =
  'h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30';

function errorText(e: unknown): string {
  return readableError(e).text;
}

/** The bar shown above the list while domains are selected. */
export function BulkBar({ count, onPick, onClear }: { count: number; onPick: (a: BulkAction) => void; onClear: () => void }) {
  const { t } = useTranslation('ux');
  if (!count) return null;
  return (
    <div className="sticky top-2 z-10 mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-card px-3 py-2 shadow-sm" role="region" aria-label={t('bulk.selected', { count })} data-bulk-bar>
      <span className="mr-1 text-sm font-medium">{t('bulk.selected', { count })}</span>
      {BULK_ACTIONS.map((a) => {
        const Icon = ICONS[a];
        return (
          <Button key={a} type="button" size="sm" variant={a === 'renew' ? 'default' : 'outline'} onClick={() => onPick(a)}>
            <Icon /> {t(`bulk.actions.${a}`)}
          </Button>
        );
      })}
      <Button type="button" size="sm" variant="ghost" className="ml-auto" onClick={onClear}>
        <X /> {t('bulk.clear')}
      </Button>
    </div>
  );
}

/** One confirmation for the chosen action, then the progress of each domain. */
export function BulkDialog({ action, domains, onClose }: { action: BulkAction | null; domains: readonly DomainSummary[]; onClose: (changed: boolean) => void }) {
  const { t } = useTranslation('ux');
  const [years, setYears] = useState(1);
  const [ns, setNs] = useState('');
  const [reason, setReason] = useState('');
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [running, setRunning] = useState(false);
  const keys = useRef<Record<string, string>>({});
  const started = Object.keys(rows).length > 0;
  const hosts = ns
    .split(/[\s,]+/)
    .map((h) => h.trim().toLowerCase().replace(/\.$/, ''))
    .filter(Boolean);

  const reset = () => {
    setRows({});
    setReason('');
    setNs('');
    setYears(1);
    keys.current = {};
  };
  const close = () => {
    if (running) return;
    const changed = Object.values(rows).some((r) => r.phase === 'ok');
    reset();
    onClose(changed);
  };

  /** Sends one domain; the key is kept per domain so a retry is the same operation. */
  const one = async (d: DomainSummary): Promise<RowState> => {
    const k = (keys.current[d.id] ??= newIdempotencyKey());
    const opts = { idempotencyKey: k };
    try {
      switch (action) {
        case 'renew': {
          const q = await api.quoteDomainRenewal(d.id, { years });
          const r = await api.renewDomain(d.id, { quote_id: q.quote_id, reason }, opts);
          return { phase: 'ok', note: t('bulk.renewed', { years: r.years }) };
        }
        case 'nameservers': {
          const r = await api.setDomainNameservers(d.id, { nameservers: hosts, reason }, opts);
          return { phase: 'ok', note: r.status === 'APPLIED' ? t('bulk.applied') : t('bulk.pending') };
        }
        case 'lock':
        case 'unlock': {
          const r = await api.setDomainTransferLock(d.id, { locked: action === 'lock', reason }, opts);
          return { phase: 'ok', note: r.status === 'APPLIED' ? t('bulk.applied') : t('bulk.pending') };
        }
        case 'syncContacts': {
          const detail = await api.getDomain(d.ascii_name);
          const subject = detail.domain.current_subject_id;
          if (!subject) return { phase: 'skipped', note: t('bulk.noSubject') };
          const r = await api.syncSubjectToDomains(subject, { domain_ids: [d.id], reason }, opts);
          const x = r.results[0];
          if (!x || x.outcome === 'UP_TO_DATE') return { phase: 'skipped', note: t('bulk.upToDate') };
          if (x.outcome === 'SKIPPED') return { phase: 'skipped', note: x.reason };
          if (x.outcome === 'REGISTRANT_CHANGE') return { phase: 'skipped', note: t('bulk.registrantChange') };
          return { phase: 'ok', note: t('bulk.startedSync') };
        }
        default:
          return { phase: 'skipped', note: '' };
      }
    } catch (e) {
      return { phase: 'failed', note: errorText(e) };
    }
  };

  const run = async (targets: readonly DomainSummary[]) => {
    setRunning(true);
    setRows((r) => ({ ...r, ...Object.fromEntries(targets.map((d) => [d.id, { phase: 'waiting' } as RowState])) }));
    for (const d of targets) {
      setRows((r) => ({ ...r, [d.id]: { phase: 'running' } }));
      const result = await one(d);
      setRows((r) => ({ ...r, [d.id]: result }));
    }
    setRunning(false);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(domains);
  };

  const done = Object.values(rows).filter((r) => r.phase !== 'waiting' && r.phase !== 'running').length;
  const ok = Object.values(rows).filter((r) => r.phase === 'ok').length;
  const failed = domains.filter((d) => rows[d.id]?.phase === 'failed');
  const valid = reason.trim().length > 0 && (action !== 'nameservers' || hosts.length >= 2);
  const label = action ? t(`bulk.actions.${action}`) : '';

  return (
    <Dialog open={action !== null} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl [&>*]:min-w-0" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t('bulk.title', { action: label, count: domains.length })}</DialogTitle>
          <DialogDescription>{t('bulk.description')}</DialogDescription>
        </DialogHeader>
        <form className="grid gap-4" onSubmit={submit}>
          {!started && action === 'renew' ? (
            <Field label={t('bulk.years')}>
              <select className={selectClass} value={years} onChange={(e) => setYears(Number(e.target.value))}>
                {[1, 2, 3, 5, 10].map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          {!started && action === 'nameservers' ? (
            <Field label={t('bulk.nameservers')} hint={t('bulk.nameserversHint')}>
              <Textarea value={ns} onChange={(e) => setNs(e.target.value)} rows={4} className="font-mono" placeholder={'ns1.example.vn\nns2.example.vn'} />
            </Field>
          ) : null}
          {!started ? (
            <Field label={t('bulk.reason')}>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} required />
            </Field>
          ) : null}
          {started ? (
            <div className="grid gap-2" aria-live="polite">
              <Progress value={(done / Math.max(1, domains.length)) * 100} />
              <p className="text-sm">{running ? t('bulk.running', { done, total: domains.length }) : t('bulk.finished', { ok, failed: failed.length })}</p>
            </div>
          ) : null}
          <ul className="max-h-72 divide-y overflow-y-auto rounded-md border text-sm">
            {domains.map((d) => {
              const r = rows[d.id];
              return (
                <li key={d.id} className="flex items-start gap-2 px-3 py-2" data-bulk-row={r?.phase ?? 'idle'}>
                  <RowIcon state={r} />
                  <Link to={`/domains/${encodeURIComponent(d.ascii_name)}`} className="min-w-0 font-medium break-all hover:underline" target="_blank" rel="noreferrer">
                    {d.unicode_name}
                  </Link>
                  <span className="ml-auto min-w-0 text-right text-xs text-muted-foreground break-words">{r ? ('note' in r && r.note ? r.note : t(`bulk.status.${r.phase}`)) : ''}</span>
                </li>
              );
            })}
          </ul>
          {!running && failed.length && started ? <Callout tone="warn">{t('bulk.finished', { ok, failed: failed.length })}</Callout> : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close} disabled={running}>
              {t('bulk.close')}
            </Button>
            {!started ? (
              <Button type="submit" disabled={!valid}>
                {t('bulk.confirm', { count: domains.length })}
              </Button>
            ) : failed.length && !running ? (
              <Button type="button" onClick={() => void run(failed)}>
                {t('bulk.retry', { count: failed.length })}
              </Button>
            ) : null}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RowIcon({ state }: { state?: RowState }) {
  const c = 'mt-0.5 size-4 shrink-0';
  if (!state || state.phase === 'waiting') return <CircleDashed className={`${c} text-muted-foreground`} aria-hidden="true" />;
  if (state.phase === 'running') return <Loader2 className={`${c} animate-spin text-primary`} aria-hidden="true" />;
  if (state.phase === 'ok') return <CheckCircle2 className={`${c} text-success`} aria-hidden="true" />;
  if (state.phase === 'failed') return <XCircle className={`${c} text-critical`} aria-hidden="true" />;
  return <CircleDashed className={`${c} text-warning`} aria-hidden="true" />;
}
